import { dateOf, esc, table } from './sbchart.ts';

export interface EmploymentCounts {
  payrollDays: number;
  due: number;
  paid: number;
  newArrears: number;
  arrearsPaid: number;
  arrearsTransferred: number;
  dispatchedDays: number;
  noAssignmentPayrollDays: number;
  injuryPayrollDays: number;
  injuryPaidPayrollDays: number;
  otherUnavailablePayrollDays: number;
}
export interface Employment {
  id: number;
  personId: number;
  firmId: number;
  firmName?: string;
  role: string;
  since: number;
  contractHiredDay?: number;
  observedSince: number;
  startedBeforeObservation: boolean;
  ended: number | null;
  endReason: string | null;
  quotedDailyWage: number;
  openingArrears: number;
  total: EmploymentCounts;
  period: EmploymentCounts;
  periodFrom: number;
  lastPayrollDay: number | null;
  lastDispatchDay: number | null;
  lastDispatchSampleDay?: number | null;
  payrollWorkedSampleDay?: number | null;
  lastPayrollWorkedFlag?: boolean | null;
  attendance?: unknown;
}
export interface EmploymentView {
  version: number;
  enabled: boolean;
  moneyUnit: 'cent';
  asOf: number;
  observedSince: number;
  scope: string;
  openingArrearsScope?: string;
  payrollScope?: string;
  current: Employment | null;
  recent: Employment[];
  arrears: { employmentId: number | null; firmId: number; amount: number; observedSince: number; preObservationUnknown: boolean }[];
  recoveries?: WageRecovery[];
  recoveryLimit?: number;
  recoveryObservedCount?: number;
}
export interface WageRecovery {
  id: number; personId: number; firmId: number; firmName?: string; observedSince: number;
  transferDay?: number | null; claimPrincipal: number; parts: { employmentId: number | null; amount: number; preObservationUnknown: boolean }[];
  caseId?: number | null; filedDay?: number | null; status: string; statusName: string;
  decisionDay?: number | null; declinedReason?: string | null; judgmentTotal?: number | null; judgmentExtra?: number | null;
  actualRecipient?: number | null; legalPayerId?: number | null; grossReceived?: number | null; workerLegalPaid?: number | null; netReceived?: number | null; unpaidAward?: number | null;
  workerLawyerId?: number | null; defendantLawyerId?: number | null; defendantLegalPaid?: number | null;
}

const amount = (v: unknown): string => typeof v === 'number' && Number.isFinite(v) ? `${(v / 100).toFixed(2)} 元` : '未知';
const day = (v: unknown): string => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? dateOf(v) : '未记录';
const count = (v: unknown): string => typeof v === 'number' && Number.isFinite(v) ? `${v} 天` : '未知';
const employer = (e: { firmId?: number; firmName?: string }): string => e.firmName ? `${e.firmName}（公司 #${e.firmId}）` : e.firmId != null ? `公司 #${e.firmId}` : '雇主未知';
const observations = (e: Employment): string => e.startedBeforeObservation
  ? `观察开始时已在此岗；从 ${day(e.observedSince)} 起记录工资与派工，更早的结算、出勤与离职过程未知。`
  : `从 ${day(e.observedSince)} 起记录本段任职的工资与派工。`;
// 说明字段原样保留在JSON中；人物页与可读文本使用同一份中文口径。
const SCOPE = '仅记录观察期间本人的雇员劳动关系。合同日薪是报价，当期实发只含本次应发中真正收到的钱，补发旧欠另列。建筑派工来自实际施工，其他行业没有独立考勤记录。总经理、银行行长的工资请查看本人实际工资流水；讨薪追偿另列，不并入新岗位当期工资实发。法院判决不等于已经收款。';
const OPENING_SCOPE = '观察或录用时已存在的欠薪，不能认定全部在本任期形成；无法查明旧任期的欠条保留“原任职未知”。';
const PAYROLL_SCOPE = '工资结算日数不等于实际到岗天数。无派工标记结算是本段观察窗口内的累计，不是连续未派工天数，也不能据此认定连续没有收入。该标记通常来自前一施工日；只有实际观察到对应施工日才显示样本日期，开局或换岗前的标记无法核实，不当作当天考勤。';
const SETTLEMENT_SCOPE = '此工资欠条记录只证明本次真实结算或移交。法院判决、赔偿、执行与诉讼费用另看已观察的追偿明细和案卷；旧记录没有的收付不补造，也不计入新岗位当期工资实发。';
const CLAIMS_TITLE = '当前工资欠条（不含已移交讨薪）';
const CLAIMS_EMPTY = '当前工资欠条为空，不代表欠薪已经付清或追回；移交讨薪也不保证已经立案。已移交金额与后续处理另看讨薪记录和案卷，观察前未记录的欠薪仍无法还原。';
function paymentSourceText(source: unknown): string {
  const names: Record<string, string> = { repay_former: '原雇主向离岗员工补发欠薪', pay_wages_backpay: '发放当期工资前补发旧欠', media_ordered_backpay: '经媒体督促后补发欠薪' };
  if (typeof source !== 'string' || !source) return '未记录';
  return names[source] ?? (/[A-Za-z_]/.test(source) ? '其他已记录的支付或讨薪处理，具体来源见原始资料' : source);
}

function countLines(c: EmploymentCounts | undefined): string[] {
  return [`实际应发 ${amount(c?.due)} · 本段当期工资实发 ${amount(c?.paid)} · 新增欠薪 ${amount(c?.newArrears)} · 补发本段旧欠 ${amount(c?.arrearsPaid)} · 移交讨薪 ${amount(c?.arrearsTransferred)}（不是到账或免债）`,
    `工资结算 ${count(c?.payrollDays)} · 实际建筑派工 ${count(c?.dispatchedDays)} · 无派工标记结算 ${count(c?.noAssignmentPayrollDays)} · 工伤应发结算 ${count(c?.injuryPayrollDays)} · 工伤有实付结算 ${count(c?.injuryPaidPayrollDays)} · 其他不可出勤结算 ${count(c?.otherUnavailablePayrollDays)}`];
}
function payrollSample(e: Employment): string {
  if (e.lastPayrollWorkedFlag == null) return '没有独立的建筑派工工资评估标记';
  const flag = e.lastPayrollWorkedFlag ? '有派工标记' : '无派工标记';
  return e.payrollWorkedSampleDay != null ? `${day(e.payrollWorkedSampleDay)} · ${flag}（最近一次发薪时采用的前一施工日记录，不是当天考勤）`
    : `${flag}；对应施工日未实际观察，不能据此证明出勤`;
}

const validDay = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
/** 日期只界定已观察日数的上限，不从累计或旧离职文字重建一条连续未派工轨迹。 */
function counterWindow(e: Employment, asOf: unknown): string[] {
  const end = e.ended ?? asOf;
  const known = validDay(e.since) && validDay(e.observedSince) && validDay(end);
  const start = known ? Math.max(e.since, e.observedSince) : null;
  const capacity = known && end >= start! ? end - start! + 1 : null;
  const lines = [capacity != null ? `累计计数观察窗口：${day(start)} → ${day(end)}，覆盖 ${capacity} 个日期（含起止日）。`
      : '累计计数观察窗口：起止资料缺失或不一致，范围未知，不按当前城市日期补算。',
    '无派工标记结算为上述窗口的累计；连续未派工天数未单独记录，不能用累计或旧离职文字倒推。'];
  const idle = e.total?.noAssignmentPayrollDays;
  if (validDay(idle) && capacity != null && idle > capacity) lines.push(`记录范围待核对：无派工标记结算累计 ${idle} 天，超过本段可观察的 ${capacity} 个日期；保留原值和离职记载，不将它当作连续未派工的证明。`);
  return lines;
}

/** 只解释已记录的工资事件，不将报价、发薪天数或旧岗补欠推成新岗实领。 */
export function employmentRecordText(data: Record<string, any>, recordDay?: number): string[] {
  const names: Record<string, string> = { start: '任职登记', period: '任职期间结账', end: '任职结束', arrears_payment: '补发欠薪', arrears_transfer: '欠薪债权转移', recovery_transfer: '登记讨薪追偿', recovery_not_filed: '讨薪未立案', recovery_filed: '讨薪立案', recovery_counsel: '讨薪律师', recovery_judgment: '讨薪判决与实际收付', recovery_no_recipient: '讨薪未找到收款人', recovery_observation: '追偿观察起点' };
  const lines = [names[data.event] ?? '其他任职记录（详见原始资料）'];
  if (data.personId != null) lines.push(`人物 #${data.personId}`);
  if (data.recovery) lines.push(...wageRecoveryText(data.recovery));
  if (!data.recovery && (data.employmentId != null || data.firmId != null)) lines.push(`${data.employmentId != null ? `任职 #${data.employmentId}` : '原任职编号未知'} · ${employer(data)}`);
  const detail = data.detail && typeof data.detail === 'object' ? data.detail : data;
  if (data.event === 'arrears_payment') {
    lines.push(`本次实际补发 ${amount(detail.amount)} · 补发后该雇主当前欠条余额 ${amount(detail.owedAfter)}`,
      '这是原雇主欠薪的实际清偿，不计为当前新岗位的当期工资。');
  }
  if (data.event === 'arrears_transfer') lines.push(`本次移交讨薪 ${amount(detail.amount)} · 移交后该雇主当前欠条余额 ${amount(detail.owedAfter)}`,
    '这是移交原有讨薪决策，不代表已经付到手、已经立案或债务免除，也不计作新岗位实发。');
  if (detail.paymentSource) lines.push(`处理来源：${paymentSourceText(detail.paymentSource)}`);
  if (detail.preObservationUnknown) lines.push('这笔欠薪的原任期在观察前，无法证明属于哪段任职。');
  if (detail.scope) lines.push(SETTLEMENT_SCOPE);
  if (data.snapshot) {
    const e = data.snapshot as Employment;
    const asOf = recordDay ?? data.asOf;
    lines.push(`${employer(e)} · ${e.role ?? '岗位未知'} · 任职 #${e.id}`, `合同日薪报价 ${amount(e.quotedDailyWage)}（不等于已经发到手）`, observations(e),
      `本段登记起日：${day(e.since)}`, ...counterWindow(e, asOf),
      `本段观察起点已欠 ${amount(e.openingArrears)}`, `本段累计：${countLines(e.total).join('；')}`,
      `${typeof asOf === 'number' && e.periodFrom > asOf ? '下一期（尚未开始）' : '本期'} ${day(e.periodFrom)} 起：${countLines(e.period).join('；')}`);
    if (e.contractHiredDay != null && e.contractHiredDay !== e.since) lines.push(`合同工龄起日 ${day(e.contractHiredDay)}（原工龄保留，不冒充本段观察历史）`);
    if (e.lastPayrollWorkedFlag != null) lines.push(`最近发薪采用的派工样本：${payrollSample(e)}`);
    if (e.ended != null) lines.push(`结束于 ${day(e.ended)} · 原因 ${e.endReason ?? '未记录，不反推'}`);
  } else if (!data.recovery && ['start', 'period', 'end'].includes(data.event)) lines.push('此条未保留任职快照，任期与累计计数窗口未知；不从旧叙述还原连续未派工天数。');
  return lines;
}

export function employmentText(view: EmploymentView | null | undefined): string[] {
  if (!view?.enabled) return ['此资料没有结构化任职观察；岗位报价不能当成实际到账，旧出勤与欠薪归属不能从文字反推。'];
  const lines = [`观察起点 ${day(view.observedSince)} · 截至 ${day(view.asOf)}`, SCOPE, OPENING_SCOPE, PAYROLL_SCOPE,
    '合同日薪是报价；实际应发、当期实发与补发欠薪分别记录。结算日数不等于实际到岗天数，新增欠薪累计不等于眼下尚欠。有实付的工伤结算日不保证工资付清。'];
  if (view.current) lines.push('当前任职', ...employmentRecordText({ event: 'start', asOf: view.asOf, snapshot: view.current }));
  else lines.push('当前没有雇员任职；经营收入、分红与旧雇主补欠另看实际流水。');
  for (const e of view.recent ?? []) if (e.id !== view.current?.id) lines.push('最近任职', ...employmentRecordText({ event: 'end', asOf: view.asOf, snapshot: e }));
  lines.push(CLAIMS_TITLE);
  if (!(view.arrears ?? []).length) lines.push(CLAIMS_EMPTY);
  for (const claim of view.arrears ?? []) lines.push(`公司 #${claim.firmId} · ${claim.employmentId == null ? '原任职编号未知' : `任职 #${claim.employmentId}`} · 尚欠 ${amount(claim.amount)} · 从 ${day(claim.observedSince)} 观察${claim.preObservationUnknown ? '；观察前形成，无法还原原任期' : ''}`);
  lines.push('已移交讨薪与实际追偿', `已展示 ${view.recoveries?.length ?? 0} / ${view.recoveryObservedCount ?? '已观察总量未知'} 条；最近 ${view.recoveryLimit ?? 8} 条，较早已归档记录另读完整历史。`);
  for (const recovery of view.recoveries ?? []) lines.push(...wageRecoveryText(recovery));
  return lines.filter(Boolean);
}

/** 追偿金额由实际入口提供；不从旧判词、旧付款数或本金优先规则倒推。 */
export function wageRecoveryText(r: WageRecovery): string[] {
  return [`追偿 #${r.id} · ${employer(r)} · ${r.statusName || '处理状态未知'}`,
    `原工人 #${r.personId} · ${r.caseId != null ? `案号 #${r.caseId}` : '案号未记录'} · 观察起点 ${day(r.observedSince)}`,
    `登记工资本金 ${amount(r.claimPrincipal)} · 移交日期 ${day(r.transferDay)} · 立案日期 ${day(r.filedDay)}`,
    ...(r.parts ?? []).map(p => `${p.employmentId != null ? `原任职 #${p.employmentId}` : '原任职未知'} · 本金 ${amount(p.amount)}${p.preObservationUnknown ? '（观察前来源未完整记录）' : ''}`),
    `处理决定日 ${day(r.decisionDay)} · 判决总额 ${amount(r.judgmentTotal)} · 判决高于工资本金部分 ${amount(r.judgmentExtra)}`,
    `实际收款人 ${r.grossReceived === 0 ? '本次没有成功到账' : r.actualRecipient != null ? `人物 #${r.actualRecipient}${r.actualRecipient !== r.personId ? '（与原工人不同）' : ''}` : '未记录'}`,
    `律师费实际付款人 ${r.legalPayerId != null ? `人物 #${r.legalPayerId}` : '未记录，不从原工人或收款人推定'}`,
    `工人律师机构 ${r.workerLawyerId != null ? `公司 #${r.workerLawyerId}` : '未记录'} · 被告律师机构 ${r.defendantLawyerId != null ? `公司 #${r.defendantLawyerId}` : '未记录'}`,
    `成功实付总额 ${amount(r.grossReceived)} · 实际律师支出 ${amount(r.workerLegalPaid)} · 扣费净到账 ${amount(r.netReceived)}`,
    `判决未实付差额 ${amount(r.unpaidAward)} · 被告实际律师支出 ${amount(r.defendantLegalPaid)}`,
    ...(r.declinedReason ? [`处理说明：${r.declinedReason}`] : []),
    '判决不是收款；实付总额可能含工资和赔偿，不臆分追回本金。扣费净到账按成功实付减实际律师支出计算，可能为负。未实付差额不是正在持续执行的余额；未记录的旧收付保留未知。'];
}

export function wageRecoveryHTML(r: WageRecovery | null | undefined): string {
  if (!r) return '<p class="meta">此案没有保留可核验的结构化追偿拆分；旧案卷仍可查看，判词和旧付款摘要不能据此当作已到账证明。</p>';
  const row = (label: string, value: string) => `<div class="sb-row"><span>${esc(label)}</span><b>${value}</b></div>`;
  const firm = (id: number | null | undefined, name: string) => id != null ? `<a data-go="firm:${id}">${esc(name)} · #${id}</a>` : '未记录';
  return row('追偿与原雇主', `#${r.id} · ${firm(r.firmId, r.firmName || '原雇主')}`) +
    row('原工人', `<a data-go="person:${r.personId}" data-view="employment">人物 #${r.personId} · 任职与工资</a>`) +
    row('处理状态', esc(r.statusName || '未知')) + row('实际关联案号', r.caseId != null ? `<a data-go="case:${r.caseId}" data-view="recovery">案号 #${r.caseId}</a>` : '未记录案号，不据此推断已经立案') +
    row('观察 / 移交 / 立案', `${day(r.observedSince)} / ${day(r.transferDay)} / ${day(r.filedDay)}`) +
    row('登记工资本金', amount(r.claimPrincipal)) +
    table(['来源任职', '对应本金', '旧史范围'], (r.parts ?? []).map(p => [p.employmentId != null ? `#${p.employmentId}` : '原任职未知', amount(p.amount), p.preObservationUnknown ? '观察前来源未完整记录' : '实际记录的任职来源'])) +
    row('处理决定日', day(r.decisionDay)) + table(['判决总额（非实收）', '判决高于工资本金部分', '判决未实付差额'], [[amount(r.judgmentTotal), amount(r.judgmentExtra), amount(r.unpaidAward)]]) +
    row('实际收款人', r.grossReceived === 0 ? '本次没有成功到账' : r.actualRecipient != null ? `<a data-go="person:${r.actualRecipient}">人物 #${r.actualRecipient}</a>${r.actualRecipient !== r.personId ? '（与原工人不同）' : ''}` : '未记录，不反推为本人收款') +
    row('律师费实际付款人', r.legalPayerId != null ? `<a data-go="person:${r.legalPayerId}">人物 #${r.legalPayerId}</a>` : '未记录，不从原工人或收款人推定') +
    table(['成功实付总额', '实际律师支出', '扣费净到账'], [[amount(r.grossReceived), amount(r.workerLegalPaid), amount(r.netReceived)]]) +
    row('工人律师 / 被告律师', `${firm(r.workerLawyerId, '工人律师机构')} / ${firm(r.defendantLawyerId, '被告律师机构')}`) + row('被告实际律师支出', amount(r.defendantLegalPaid)) +
    (r.declinedReason ? row('处理说明', esc(r.declinedReason)) : '') +
    '<p class="meta">判决不等于收款，成功实付可能同时包含工资与赔偿，不按本金优先臆分。扣费净到账按成功实付减实际律师支出计算，可能为负。未实付差额只比较判决与成功付款，不代表正在持续执行；律师支出分别列示，不混作工人净收入。旧史未知不补造。</p>';
}

/** 人物页只使用有界只读视图，不额外查询全城公司或历史。 */
export function employmentHTML(view: EmploymentView | null | undefined, opened: ReadonlySet<string> = new Set()): string {
  if (!view?.enabled) return `<p class="meta">${esc(employmentText(view)[0])}</p>`;
  const row = (label: string, value: string) => `<div class="sb-row"><span>${esc(label)}</span><b>${value}</b></div>`;
  const section = (title: string, body: string) => `<section class="sb-sec"><h4>${esc(title)}</h4>${body}</section>`;
  const counts = (c: EmploymentCounts | undefined) => table(['实际应发', '当期工资实发', '新增欠薪', '补发本段旧欠', '移交讨薪（非实收）'], [[amount(c?.due), amount(c?.paid), amount(c?.newArrears), amount(c?.arrearsPaid), amount(c?.arrearsTransferred)]]) +
    table(['工资结算日', '实际建筑派工日', '无派工标记结算日', '工伤应发结算日', '工伤有实付结算日', '其他不可出勤结算日'], [[count(c?.payrollDays), count(c?.dispatchedDays), count(c?.noAssignmentPayrollDays), count(c?.injuryPayrollDays), count(c?.injuryPaidPayrollDays), count(c?.otherUnavailablePayrollDays)]]);
  const segment = (e: Employment) => row('雇主与任职', `<a data-go="firm:${e.firmId}">${esc(employer(e))}</a> · 任职 #${esc(e.id)} · ${esc(e.role)}`) +
    row('合同日薪报价', `${esc(amount(e.quotedDailyWage))}（不等于实领）`) +
    row('本段登记起日', esc(day(e.since))) +
    (e.contractHiredDay != null && e.contractHiredDay !== e.since ? row('合同工龄起日', `${esc(day(e.contractHiredDay))}（原工龄保留，不冒充本段观察历史）`) : '') +
    `<p class="meta">${esc(observations(e))}</p>` +
    counterWindow(e, view.asOf).map(line => `<p class="${line.startsWith('记录范围待核对') ? 'bad' : 'meta'}">${esc(line)}</p>`).join('') +
    row('观察起点已欠', esc(amount(e.openingArrears))) +
    row('最近工资结算', esc(day(e.lastPayrollDay))) + row('最近建筑派工', esc(day(e.lastDispatchDay))) +
    (e.lastPayrollWorkedFlag != null ? row('最近工资评估的派工样本', esc(payrollSample(e))) : '') +
    (e.attendance == null ? '<p class="meta">没有独立的非建筑到岗考勤；不把工资结算日当成实际出勤。</p>' : '') +
    section(`${e.periodFrom > view.asOf ? '下一期（尚未开始）' : '本期'} · ${day(e.periodFrom)} 起`, counts(e.period)) + section('本段观察以来累计', counts(e.total)) +
    (e.ended != null ? row('任职结束', `${esc(day(e.ended))} · ${esc(e.endReason ?? '未记录，不反推')}`) : '');
  const current = view.current ? section('当前任职', segment(view.current)) : '<p class="sb-empty">当前没有雇员任职；经营收入、分红与旧雇主补欠另计。</p>';
  const recent = (view.recent ?? []).filter(e => e.id !== view.current?.id);
  const claims = (view.arrears ?? []).length ? table(['原欠薪雇主', '归属任职', '当前尚欠', '观察范围'], view.arrears.map(c => [
    `<a data-go="firm:${c.firmId}">公司 #${c.firmId}</a>`, c.employmentId == null ? '原任职未知' : `任职 #${c.employmentId}`, amount(c.amount),
    `${day(c.observedSince)} 起${c.preObservationUnknown ? '；观察前欠薪的原任期未知' : ''}`,
  ])) : `<p class="meta">${esc(CLAIMS_EMPTY)}</p>`;
  const recoveries = section('已移交讨薪与实际追偿', `<p class="meta">已展示 ${view.recoveries?.length ?? 0} / ${view.recoveryObservedCount ?? '已观察总量未知'} 条；最近 ${view.recoveryLimit ?? 8} 条，较早已归档记录另读完整历史。</p>` +
    (view.recoveries?.length ? view.recoveries.map(r => {
      const key = `wageRecovery:${r.personId}:${r.id}`;
      return `<details data-key="${key}"${opened.has(key) ? ' open' : ''}><summary>追偿 #${r.id} · ${esc(employer(r))} · ${esc(r.statusName || '状态未知')}${r.caseId != null ? ` · 案号 #${r.caseId}` : ''}</summary>${wageRecoveryHTML(r)}</details>`;
    }).join('') : '<p class="meta">当前热窗口没有可读追偿记录，不代表欠薪已经付清，也不证明从未移交或立案。</p>'));
  return `<p class="meta">观察起点 ${esc(day(view.observedSince))} · 截至 ${esc(day(view.asOf))}。${esc(SCOPE)}</p>` + current +
    recent.map(e => {
      const key = `employment:${e.personId}:${e.id}`;
      return `<details data-key="${esc(key)}"${opened.has(key) ? ' open' : ''}><summary>最近任职 #${esc(e.id)} · ${esc(employer(e))} · ${esc(e.role)}</summary>${segment(e)}</details>`;
    }).join('') +
    section(CLAIMS_TITLE, claims) +
    recoveries +
    `<p class="meta">${esc(OPENING_SCOPE)}</p><p class="meta">${esc(PAYROLL_SCOPE)}</p>` +
    '<p class="meta">当期实发只归本段任职；补发欠薪单列。新增欠薪是本期发生额，不是当前应收余额。移交讨薪不代表已追回或免债；判赔收付只按已观察的追偿记录展示，后续执行没有全链追踪。有实付的工伤结算日不保证付清。更早任职与共同记录编号可从完整历史读取。</p>';
}
